import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, Share2, MessageCircle, MapPin, Truck, Calendar, Weight, Dna, ChevronLeft, ChevronRight, Send, Bot, Star, Shield } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { BuyerCommitmentModal } from '../components/BuyerCommitmentModal';
import { toast } from 'sonner';

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showCommitment, setShowCommitment] = useState(false);
  const [commitmentAction, setCommitmentAction] = useState('purchase');

  useEffect(() => {
    loadListing();
  }, [id]);

  const loadListing = async () => {
    try {
      const data = await api.getListing(id);
      setListing(data);
    } catch (e) {
      toast.error('Anuncio no encontrado');
      navigate('/explorar');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      if (listing.is_favorited) {
        await api.removeFavorite(id);
        setListing({ ...listing, is_favorited: false, favorites_count: listing.favorites_count - 1 });
        toast.success('Eliminado de favoritos');
      } else {
        await api.addFavorite(id);
        setListing({ ...listing, is_favorited: true, favorites_count: listing.favorites_count + 1 });
        toast.success('Agregado a favoritos');
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleSendMessage = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await api.sendMessage({
        receiver_id: listing.seller_id,
        listing_id: id,
        content: message
      });
      toast.success('Mensaje enviado');
      setMessage('');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const handleAIChat = async () => {
    if (!aiMessage.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await api.chatWithAI(listing.seller_id, {
        content: aiMessage,
        listing_id: id
      });
      setAiResponse(response.response);
      setAiMessage('');
    } catch (e) {
      toast.error('El asistente IA no está disponible');
    } finally {
      setAiLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] pt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square skeleton rounded-2xl" />
            <div className="space-y-4">
              <div className="h-8 w-3/4 skeleton rounded" />
              <div className="h-6 w-1/4 skeleton rounded" />
              <div className="h-32 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const images = listing.images?.length > 0 ? listing.images : ['/placeholder-animal.jpg'];

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="listing-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/explorar" className="text-zinc-500 hover:text-zinc-300">Explorar</Link>
          <span className="text-zinc-700">/</span>
          {listing.category_name && (
            <>
              <Link to={`/categoria/${listing.category_id}`} className="text-zinc-500 hover:text-zinc-300">
                {listing.category_name}
              </Link>
              <span className="text-zinc-700">/</span>
            </>
          )}
          <span className="text-zinc-400 truncate">{listing.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#121215]">
              <img 
                src={images[currentImage]} 
                alt={listing.title}
                className="w-full h-full object-cover"
              />
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {listing.is_demo && (
                  <span className="bg-amber-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded tracking-wider uppercase backdrop-blur-sm z-10" data-testid="detail-demo-badge">
                    Demo
                  </span>
                )}
                {listing.is_premium && (
                  <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 backdrop-blur-md rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    Premium
                  </span>
                )}
                {listing.is_featured && (
                  <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 backdrop-blur-md rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    Destacado
                  </span>
                )}
              </div>
              {/* Demo Watermark */}
              {listing.is_demo && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
                  <span className="text-white/20 text-6xl font-black tracking-[0.3em] uppercase rotate-[-25deg] select-none">
                    PRUEBA
                  </span>
                </div>
              )}

              {/* Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImage(i => i === 0 ? images.length - 1 : i - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentImage(i => i === images.length - 1 ? 0 : i + 1)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setCurrentImage(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      currentImage === i ? 'border-purple-500' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            {/* Title & Price */}
            <div>
              <h1 className="text-2xl md:text-3xl font-medium text-zinc-100 mb-2 font-[Outfit]">
                {listing.title}
              </h1>
              <div className="flex items-center gap-4">
                <span className="text-3xl font-semibold text-zinc-100 font-[Outfit]">
                  {formatPrice(listing.price)}
                </span>
                {listing.quantity > 1 && (
                  <span className="text-sm text-zinc-500">
                    ({listing.quantity} disponibles)
                  </span>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {listing.sex && (
                <div className="glass-card p-3 text-center">
                  <span className="text-xs text-zinc-500 block mb-1">Sexo</span>
                  <span className="text-sm text-zinc-200 capitalize">{listing.sex}</span>
                </div>
              )}
              {listing.age && (
                <div className="glass-card p-3 text-center">
                  <span className="text-xs text-zinc-500 block mb-1">Edad</span>
                  <span className="text-sm text-zinc-200">{listing.age}</span>
                </div>
              )}
              {listing.weight && (
                <div className="glass-card p-3 text-center">
                  <span className="text-xs text-zinc-500 block mb-1">Peso</span>
                  <span className="text-sm text-zinc-200">{listing.weight}</span>
                </div>
              )}
              {listing.hatch_date && (
                <div className="glass-card p-3 text-center">
                  <span className="text-xs text-zinc-500 block mb-1">Nacimiento</span>
                  <span className="text-sm text-zinc-200">{listing.hatch_date}</span>
                </div>
              )}
            </div>

            {/* Genes & Traits */}
            {(listing.genes?.length > 0 || listing.traits?.length > 0) && (
              <div className="space-y-3">
                {listing.genes?.length > 0 && (
                  <div>
                    <span className="text-xs text-zinc-500 block mb-2">Genes</span>
                    <div className="flex flex-wrap gap-2">
                      {listing.genes.map((gene) => (
                        <span key={gene} className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm">
                          {gene}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {listing.traits?.length > 0 && (
                  <div>
                    <span className="text-xs text-zinc-500 block mb-2">Traits</span>
                    <div className="flex flex-wrap gap-2">
                      {listing.traits.map((trait) => (
                        <span key={trait} className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-zinc-300 mb-2">Descripción</h3>
              <p className="text-zinc-400 whitespace-pre-wrap">{listing.description}</p>
            </div>

            {/* Shipping */}
            <div className="flex items-center gap-4 text-sm">
              {listing.shipping_available && (
                <span className="flex items-center gap-2 text-emerald-400">
                  <Truck className="w-4 h-4" />
                  Envío disponible
                </span>
              )}
              {listing.local_pickup && (
                <span className="flex items-center gap-2 text-blue-400">
                  <MapPin className="w-4 h-4" />
                  Entrega local
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={handleFavorite}
                variant={listing.is_favorited ? "default" : "outline"}
                className={listing.is_favorited ? "bg-red-500/20 text-red-400 border-red-500/30" : "border-white/10 text-zinc-300"}
                data-testid="favorite-btn"
              >
                <Heart className={`w-4 h-4 mr-2 ${listing.is_favorited ? 'fill-current' : ''}`} />
                {listing.favorites_count}
              </Button>
              {user && listing.seller_id !== user?.id && (
                <>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    onClick={() => { setCommitmentAction('purchase'); setShowCommitment(true); }}
                    data-testid="buy-btn"
                  >
                    Comprar
                  </Button>
                  <Button
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                    onClick={() => { setCommitmentAction('protected_purchase'); setShowCommitment(true); }}
                    data-testid="protected-buy-btn"
                  >
                    <Shield className="w-4 h-4 mr-1" /> Compra Protegida
                  </Button>
                </>
              )}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-500">Compartir por:</span>
              <button
                data-testid="share-whatsapp-btn"
                onClick={() => {
                  const ts = Date.now();
                  const ogUrl = `${window.location.origin}/api/og/anuncio/${listing.id}?v=${ts}`;
                  const text = encodeURIComponent(`${listing.title} - $${listing.price?.toLocaleString('es-MX')} MXN en MorphMX\n${ogUrl}`);
                  window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                title="WhatsApp"
              >
                <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </button>
              <button
                data-testid="share-btn"
                onClick={async () => {
                  const ts = Date.now();
                  const ogUrl = `${window.location.origin}/api/og/anuncio/${listing.id}?v=${ts}`;
                  try {
                    await navigator.clipboard.writeText(ogUrl);
                    toast.success('Enlace copiado al portapapeles');
                  } catch {
                    const textArea = document.createElement('textarea');
                    textArea.value = ogUrl;
                    textArea.style.position = 'fixed';
                    textArea.style.opacity = '0';
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    toast.success('Enlace copiado al portapapeles');
                  }
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                title="Copiar enlace"
              >
                <Share2 className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {/* Seller Card */}
            <div className="glass-card p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center text-xl">
                  {listing.seller_avatar ? (
                    <img src={listing.seller_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    listing.seller_name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <Link 
                    to={`/criador/${listing.seller_id}`}
                    className="text-lg font-medium text-zinc-100 hover:text-white"
                  >
                    {listing.seller_name}
                  </Link>
                  {listing.is_premium && (
                    <span className="flex items-center gap-1 text-xs text-purple-400 mt-1">
                      <Shield className="w-3 h-3" />
                      Vendedor Premium
                    </span>
                  )}
                </div>
              </div>

              {/* Contact Form */}
              <div className="space-y-3">
                <Textarea
                  placeholder="Escribe tu mensaje..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-500 resize-none"
                  rows={3}
                  data-testid="contact-message"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={sending || !message.trim()}
                  className="w-full bg-zinc-100 text-zinc-900 hover:bg-white"
                  data-testid="send-message-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {sending ? 'Enviando...' : 'Enviar mensaje'}
                </Button>
              </div>
            </div>

            {/* AI Chat (if premium seller) */}
            {listing.is_premium && (
              <div className="glass-card p-6 border-purple-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-zinc-100">Asistente IA</h3>
                    <p className="text-xs text-zinc-500">Respuestas instantáneas 24/7</p>
                  </div>
                </div>

                {aiResponse && (
                  <div className="mb-4 p-4 rounded-xl bg-purple-500/5 border border-purple-500/10">
                    <p className="text-sm text-zinc-300">{aiResponse}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Pregunta sobre este animal..."
                    value={aiMessage}
                    onChange={(e) => setAiMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAIChat()}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-purple-500/50"
                    data-testid="ai-chat-input"
                  />
                  <Button 
                    onClick={handleAIChat}
                    disabled={aiLoading || !aiMessage.trim()}
                    className="bg-purple-600 hover:bg-purple-500"
                    data-testid="ai-chat-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buyer Commitment Modal */}
      <BuyerCommitmentModal
        isOpen={showCommitment}
        onClose={() => setShowCommitment(false)}
        onConfirm={async () => {
          if (commitmentAction === 'protected_purchase') {
            try {
              const result = await api.createProtectedPurchase({
                listing_id: id,
                origin_url: window.location.origin,
              });
              if (result.url) window.location.href = result.url;
            } catch (e) {
              toast.error(formatApiError(e));
            }
          } else {
            toast.success('Compromiso registrado. Contacta al vendedor para completar la transacción.');
          }
        }}
        actionType={commitmentAction}
        listingId={id}
      />
    </div>
  );
}
